import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { CartProvider, useCart } from "@/contexts/CartContext";
import CartDrawer from "@/components/CartDrawer";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import BrandStyle from "@/components/BrandStyle";
import BrandLogo from "@/components/BrandLogo";
import VisualConfigurator from "@/components/VisualConfigurator";
import { resolveUrl } from "@/components/ImageUploader";
import { ArrowLeft, ArrowRight, ChevronRight, ShoppingBag, Truck, Sparkles, CheckCircle2, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmtEuro = (n) => "€ " + (Number(n) || 0).toFixed(2).replace(".", ",");

export default function StorefrontStyleWrapper() {
  const { slug } = useParams();
  return (
    <CartProvider slug={slug}>
      <StorefrontStyle />
      <CartDrawer />
    </CartProvider>
  );
}

function StorefrontStyle() {
  const { slug, sku_madre } = useParams();
  const navigate = useNavigate();
  const cart = useCart();
  const [data, setData] = useState(null);
  const [composition, setComposition] = useState(null);

  useEffect(() => {
    setData(null);
    axios.get(`${API}/public/storefront/${slug}/style/${sku_madre}`).then((r) => setData(r.data)).catch(() => {});
  }, [slug, sku_madre]);

  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const { tenant, network, modello, lane_riv, lane_cm, configurator_options, models_in_category } = data;
  const brand = tenant.settings?.brand_kit || {};
  const accent = brand.secondary_color || brand.primary_color || "#C25934";
  const heroImg = modello.immagini?.[0];

  const currentIdx = (models_in_category || []).findIndex((m) => m.sku_madre === sku_madre);
  const prevModel = currentIdx > 0 ? models_in_category[currentIdx - 1] : null;
  const nextModel = currentIdx >= 0 && currentIdx < models_in_category.length - 1 ? models_in_category[currentIdx + 1] : null;

  const addToCart = (variant) => {
    cart.add(variant.sku_variante, 1, {
      nome: modello.nome,
      image: variant.immagini?.[0] || heroImg,
      custom_attributes: variant.custom_attributes,
    });
    toast.success(`${modello.nome} aggiunto al carrello`);
  };

  const addCustomToCart = () => {
    if (!composition) return;
    cart.add(composition.skuRuntime, 1, {
      nome: `${modello.nome} (personalizzato)`,
      image: heroImg,
      custom_attributes: composition.selezioni,
      prezzo_custom: composition.prezzoTotale,
    });
    toast.success(`Configurazione personalizzata aggiunta · ${fmtEuro(composition.prezzoTotale)}`);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="storefront-style" data-storefront="true">
      <BrandStyle brand={brand} />

      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between gap-4">
          <Link to={`/s/${slug}`} data-testid="style-back" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Indietro
          </Link>
          <BrandLogo
            logoUrl={brand.logo_url}
            fallbackText={tenant.name}
            resolveUrl={resolveUrl}
            className="h-8"
            dataTestid="storefront-brand-logo"
          />
          <LanguageSwitcher />
        </div>
      </header>

      {/* Breadcrumb / sitemap */}
      <nav className="max-w-7xl mx-auto px-6 lg:px-12 pt-5 text-[11px] tracking-wider uppercase text-muted-foreground flex items-center gap-1 flex-wrap" data-testid="breadcrumb">
        <Link to={`/s/${slug}`} className="hover:text-foreground">Home</Link>
        <ChevronRight className="w-3 h-3" />
        <span>{network.display_name}</span>
        <ChevronRight className="w-3 h-3" />
        <span className="capitalize">{modello.categoria}</span>
        <ChevronRight className="w-3 h-3" />
        <span className="text-foreground font-medium">{modello.nome}</span>
      </nav>

      {/* Hero modello */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-6 pb-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          <ProductGallery images={modello.immagini || []} alt={modello.nome} />
          <div>
            <div className="overline text-brand mb-3" style={{ color: accent }}>{modello.categoria}</div>
            <h1 className="editorial-h1 text-4xl lg:text-6xl mb-3" data-testid="style-name">{modello.nome}</h1>
            <p className="text-lg text-muted-foreground mb-6 max-w-md">{modello.descrizione_short}</p>
            <div className="text-sm text-muted-foreground">
              <span className="text-foreground font-heading font-bold text-3xl">{fmtEuro(modello.prezzo_da)}</span>
              <span className="ml-1">a partire da</span>
            </div>
            {/* Prev/Next navigation in stessa categoria */}
            <div className="flex items-center gap-3 mt-8" data-testid="prev-next-nav">
              <Link to={prevModel ? `/s/${slug}/style/${prevModel.sku_madre}` : "#"} data-testid="nav-prev"
                className={`flex-1 h-10 px-3 rounded-sm border border-border text-xs inline-flex items-center gap-2 ${!prevModel ? "opacity-40 pointer-events-none" : "hover:bg-accent"}`}>
                <ArrowLeft className="w-3.5 h-3.5" />
                <span className="truncate">{prevModel ? prevModel.nome : "—"}</span>
              </Link>
              <Link to={nextModel ? `/s/${slug}/style/${nextModel.sku_madre}` : "#"} data-testid="nav-next"
                className={`flex-1 h-10 px-3 rounded-sm border border-border text-xs inline-flex items-center justify-end gap-2 ${!nextModel ? "opacity-40 pointer-events-none" : "hover:bg-accent"}`}>
                <span className="truncate">{nextModel ? nextModel.nome : "—"}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Lane 🟢 Pronta consegna rivenditore */}
      {lane_riv.length > 0 && (
        <Lane
          color="emerald"
          icon={<Truck className="w-5 h-5" />}
          title="🟢 Pronta consegna"
          subtitle={`Disponibili subito presso ${tenant.name} · spedizione 1-2 gg`}
          items={lane_riv}
          accent={accent}
          slug={slug}
          onAdd={addToCart}
          testid="lane-riv"
        />
      )}

      {/* Lane 🟡 Pronto presso Casa Madre */}
      {lane_cm.length > 0 && (
        <Lane
          color="amber"
          icon={<Clock className="w-5 h-5" />}
          title="🟡 Pronto presso Casa Madre"
          subtitle={`Configurazioni preimpostate · spedizione ${modello.lead_time_pronta_cm_gg || 5} gg lavorativi`}
          items={lane_cm}
          accent={accent}
          slug={slug}
          onAdd={addToCart}
          testid="lane-cm"
        />
      )}

      {/* Caratteristiche / scheda tecnica (visibili in vetrina) */}
      <ProductSpecs modello={modello} />

      {/* Configuratore a liste (sempre presente): scegli tessuto/piede/metallo,
          prezzo dinamico + Prenota (se match SKU) / Richiedi configurazione. */}
      <QuickConfigurator modello={modello} tenant={tenant} accent={accent}
        laneCm={lane_cm} laneRiv={lane_riv} />

      {/* Banner 🟠 — Configuratore VISIVO opzionale (Surface Editor) — solo se attivato */}
      {modello.configurator_enabled && (
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-10" data-testid="lane-personalizza">
          <div className="bg-foreground text-white rounded-sm p-8 lg:p-12 flex flex-col lg:flex-row items-center gap-6 lg:gap-12" style={{ borderLeft: `6px solid ${accent}` }}>
            <Sparkles className="w-10 h-10 lg:w-14 lg:h-14 flex-shrink-0" style={{ color: accent }} />
            <div className="flex-1 text-center lg:text-left">
              <div className="overline mb-2" style={{ color: accent }}>🟠 Anteprima visiva su misura · {modello.lead_time_configuratore_gg}-{(modello.lead_time_configuratore_gg || 60) + 30} gg</div>
              <h2 className="editorial-h2 text-3xl lg:text-5xl font-heading mb-3">Anteprima visiva della tua <em style={{ color: accent }}>{modello.nome}</em></h2>
              <p className="text-white/70 max-w-2xl">Configuratore avanzato con anteprima grafica dei materiali. Da <strong className="text-white">{fmtEuro(modello.prezzo_base)}</strong>.</p>
            </div>
            <a href={`/s/${tenant.slug}/configura/${modello.sku_madre}`} data-testid="open-configurator"
              className="h-12 px-6 rounded-sm text-sm font-medium inline-flex items-center gap-2 hover:opacity-90 transition-opacity"
              style={{ background: accent, color: "#0F1B14" }}>
              <Sparkles className="w-4 h-4" /> Apri anteprima visiva
            </a>
          </div>
        </section>
      )}

      {!lane_riv.length && !lane_cm.length && !modello.configurator_enabled && (
        <div className="max-w-3xl mx-auto px-6 py-20 text-center text-muted-foreground">
          <p>Nessuna variante disponibile per questo modello al momento.</p>
        </div>
      )}
    </div>
  );
}

const Lane = ({ color, icon, title, subtitle, items, accent, slug, onAdd, testid }) => {
  const palette = {
    emerald: { bg: "bg-emerald-50/60", border: "border-emerald-200", text: "text-emerald-900" },
    amber: { bg: "bg-amber-50/60", border: "border-amber-200", text: "text-amber-900" },
  }[color] || { bg: "bg-neutral-50", border: "border-border", text: "text-foreground" };
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-6" data-testid={testid}>
      <div className={`rounded-sm border-2 ${palette.border} ${palette.bg} p-5 lg:p-7`}>
        <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
          <div>
            <div className={`flex items-center gap-2 ${palette.text}`}>{icon}<h3 className="font-heading font-bold text-2xl">{title}</h3></div>
            <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${palette.text} bg-white border ${palette.border}`}>{items.length} disponibili</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => {
            const combo = Object.entries(it.combination || {}).slice(0, 3);
            return (
              <div key={it.sku_variante} className="bg-white rounded-sm overflow-hidden border border-border flex flex-col" data-testid={`item-${it.sku_variante}`}>
                <Link to={`/s/${slug}/product/${it.sku_variante}`} className="block aspect-[4/3] bg-neutral-100 overflow-hidden">
                  {it.immagini?.[0] && <img src={resolveUrl(it.immagini[0])} alt={it.sku_variante} className="w-full h-full object-cover hover:scale-105 transition-transform" />}
                </Link>
                <div className="p-3 flex-1 flex flex-col">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{it.sku_variante}</div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {combo.map(([k, v]) => (
                      <span key={k} className="text-[10px] px-1.5 py-0.5 bg-accent/40 rounded-full">{v}</span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-auto pt-2">
                    <div>
                      <div className="font-heading font-bold text-lg">{fmtEuro(it.prezzo_finale)}</div>
                      {it.stock_disponibile > 0 && <div className="text-[10px] text-emerald-700">{it.stock_disponibile} disponibili</div>}
                    </div>
                    <button onClick={() => onAdd(it)} data-testid={`add-${it.sku_variante}`}
                      className="h-9 px-3 rounded-sm text-white text-xs font-medium inline-flex items-center gap-1.5 hover:opacity-90"
                      style={{ background: accent }}>
                      <ShoppingBag className="w-3.5 h-3.5" /> Aggiungi
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};


function ProductGallery({ images, alt }) {
  const [idx, setIdx] = useState(0);
  const list = Array.isArray(images) ? images.filter(Boolean) : [];
  const cur = list[Math.min(idx, list.length - 1)] || null;

  return (
    <div className="space-y-2" data-testid="product-gallery">
      <div className="aspect-[4/3] bg-neutral-100 rounded-sm overflow-hidden">
        {cur ? (
          <img src={resolveUrl(cur)} alt={alt} className="w-full h-full object-cover" data-testid="gallery-main" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground/40">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
            <span className="text-[10px] uppercase tracking-wider mt-2 opacity-70">Foto in arrivo</span>
          </div>
        )}
      </div>
      {list.length > 1 && (
        <div className="grid grid-cols-6 gap-1.5" data-testid="gallery-thumbs">
          {list.map((u, i) => (
            <button key={`${u}-${i}`} onClick={() => setIdx(i)}
              className={`aspect-square bg-neutral-100 rounded-sm overflow-hidden border-2 transition-all ${i === idx ? "border-foreground" : "border-transparent hover:border-border"}`}
              data-testid={`gallery-thumb-${i}`}>
              <img src={resolveUrl(u)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function ProductSpecs({ modello }) {
  const fv = modello?.feature_visibility || {};
  const dim = modello?.dimensioni || {};
  const dimEntries = [
    ["larghezza_cm", "Larghezza", "cm"],
    ["profondita_cm", "Profondità", "cm"],
    ["altezza_cm", "Altezza", "cm"],
    ["diametro_cm", "Diametro Ø", "cm"],
    ["seduta_cm", "H. seduta", "cm"],
    ["peso_kg", "Peso", "kg"],
  ].filter(([k]) => dim[k] !== undefined && dim[k] !== null && dim[k] !== "");
  const mat = Array.isArray(modello?.materiali) ? modello.materiali : [];
  const specs = Array.isArray(modello?.specifiche) ? modello.specifiche : [];
  const hasDesc = fv.descrizione !== false && modello?.descrizione_long;
  const hasDim = fv.dimensioni !== false && dimEntries.length > 0;
  const hasMat = fv.materiali !== false && mat.length > 0;
  const hasSpec = fv.specifiche !== false && specs.length > 0;
  const hasGar = fv.garanzia !== false && modello?.garanzia_mesi;
  const hasLead = fv.lead_time !== false && modello?.lead_time_gg;
  if (!hasDesc && !hasDim && !hasMat && !hasSpec && !hasGar && !hasLead) return null;
  return (
    <section className="bg-neutral-50 border-y border-border" data-testid="style-specs">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          {hasDesc && (
            <div data-testid="style-descrizione">
              <div className="overline text-muted-foreground mb-3">Descrizione</div>
              <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90">{modello.descrizione_long}</p>
            </div>
          )}
          {hasMat && (
            <div data-testid="style-materiali">
              <div className="overline text-muted-foreground mb-3">Materiali</div>
              <div className="flex flex-wrap gap-1.5">
                {mat.map((m, i) => (
                  <span key={i} className="px-2.5 py-1 bg-white border border-border rounded-sm text-xs font-medium">{m}</span>
                ))}
              </div>
            </div>
          )}
        </div>
        <aside className="space-y-6">
          {hasDim && (
            <div data-testid="style-dimensioni">
              <div className="overline text-muted-foreground mb-3">Dimensioni</div>
              <dl className="grid grid-cols-2 gap-y-2 gap-x-3 text-sm">
                {dimEntries.map(([k, label, unit]) => (
                  <React.Fragment key={k}>
                    <dt className="text-muted-foreground text-xs">{label}</dt>
                    <dd className="font-mono font-medium text-right">{dim[k]} <span className="text-[10px] text-muted-foreground">{unit}</span></dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
          )}
          {hasSpec && (
            <div data-testid="style-specifiche">
              <div className="overline text-muted-foreground mb-3">Specifiche tecniche</div>
              <dl className="space-y-1 text-sm">
                {specs.map((s, i) => (
                  <div key={i} className="flex justify-between gap-3 border-b border-border/60 pb-1.5">
                    <dt className="text-muted-foreground text-xs flex-shrink-0">{s.key}</dt>
                    <dd className="font-medium text-right text-xs">{s.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
          {(hasGar || hasLead) && (
            <div data-testid="style-meta" className="bg-white rounded-sm p-4 space-y-2 border border-border">
              {hasGar && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">🛡️ Garanzia</span>
                  <span className="font-medium">{modello.garanzia_mesi} mesi</span>
                </div>
              )}
              {hasLead && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">⏱ Tempi di consegna</span>
                  <span className="font-medium">{modello.lead_time_gg} giorni</span>
                </div>
              )}
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}


// ══════════════ QuickConfigurator ══════════════
// Configuratore a liste (senza rendering visivo). Prezzo dinamico + doppio CTA:
//   • se combo scelta matcha uno SKU realizzato in laneCm/laneRiv → "Prenota subito"
//   • altrimenti → "Richiedi questa configurazione" (apre form)
function QuickConfigurator({ modello, tenant, accent, laneCm = [], laneRiv = [] }) {
  const rawAxes = Array.isArray(modello?.variant_axes) ? modello.variant_axes : [];
  // Normalizza: alcuni seed usano `key`, altri `name`
  const axes = rawAxes.map((ax) => ({ ...ax, name: ax.name || ax.key, label: ax.label || ax.name || ax.key }));
  const [choices, setChoices] = useState({});
  const [showForm, setShowForm] = useState(false);

  // Preseleziona la prima opzione di ogni asse
  useEffect(() => {
    const init = {};
    for (const ax of axes) {
      const opts = ax?.options || [];
      if (opts.length) init[ax.name] = opts[0].value;
    }
    setChoices(init);
  }, [modello?.sku_madre]);

  if (!axes.length) return null;

  const currentDelta = axes.reduce((sum, ax) => {
    const opt = (ax.options || []).find((o) => o.value === choices[ax.name]);
    return sum + (opt?.price_delta || opt?.sovrapprezzo || 0);
  }, 0);
  const prezzoBase = Number(modello.prezzo_base) || 0;
  const prezzoIndicativo = prezzoBase + currentDelta;

  // Verifica match con SKU realizzati (laneCm + laneRiv) — se combination compatibile
  const currentCombination = {};
  for (const ax of axes) currentCombination[ax.name] = choices[ax.name];
  const matchSku = [...laneCm, ...laneRiv].find((v) => {
    if (!v?.combination) return false;
    return Object.entries(currentCombination).every(([k, val]) => v.combination[k] === val);
  });

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-10" data-testid="quick-configurator">
      <div className="bg-white border border-border rounded-sm p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-sm inline-flex items-center justify-center" style={{ background: accent + "22", color: accent }}>
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <div className="overline text-muted-foreground">Configurazione rapida</div>
            <h2 className="editorial-h2 text-2xl font-heading">Scegli i materiali</h2>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          {axes.map((ax) => (
            <AxisPicker key={ax.name} axis={ax}
              selected={choices[ax.name]}
              onSelect={(v) => setChoices((c) => ({ ...c, [ax.name]: v }))} />
          ))}
        </div>

        <div className="mt-6 pt-5 border-t border-border grid lg:grid-cols-2 gap-4 items-end">
          <div>
            <div className="overline text-muted-foreground mb-1">
              {matchSku ? "Prezzo confermato" : "Prezzo indicativo"}
            </div>
            <div className="font-heading font-bold text-3xl" data-testid="quick-price">{fmtEuro(prezzoIndicativo)}</div>
            {!matchSku && (
              <div className="text-[11px] text-muted-foreground mt-1">
                Prezzo definitivo confermato da Casa Madre entro 24-48h.
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {matchSku && (
              <a href={`/s/${tenant.slug}/style/${modello.sku_madre}#sku-${matchSku.sku_variante}`}
                data-testid="cta-book-now"
                className="flex-1 h-12 rounded-sm text-sm font-medium inline-flex items-center justify-center gap-2"
                style={{ background: accent, color: "#0F1B14" }}>
                <ShoppingBag className="w-4 h-4" /> Prenota subito ({matchSku.sku_variante})
              </a>
            )}
            <button onClick={() => setShowForm(true)}
              data-testid="cta-request-config"
              className={`flex-1 h-12 rounded-sm text-sm font-medium inline-flex items-center justify-center gap-2 border border-foreground ${matchSku ? "bg-white text-foreground hover:bg-neutral-100" : "bg-foreground text-white hover:bg-black"}`}>
              <Sparkles className="w-4 h-4" /> {matchSku ? "Richiedi variazione" : "Richiedi questa configurazione"}
            </button>
          </div>
        </div>
      </div>

      {showForm && (
        <ConfigRequestForm
          modello={modello}
          tenant={tenant}
          axes={axes}
          choices={choices}
          prezzoIndicativo={prezzoIndicativo}
          leadTimeIndicativo={modello.lead_time_gg || 45}
          onClose={() => setShowForm(false)} />
      )}
    </section>
  );
}

function AxisPicker({ axis, selected, onSelect }) {
  const opts = axis.options || [];
  return (
    <div data-testid={`axis-${axis.name}`}>
      <div className="overline text-muted-foreground mb-2">{axis.label || axis.name}</div>
      <div className="grid grid-cols-3 gap-1.5 max-h-60 overflow-auto pr-1">
        {opts.map((o) => {
          const isSel = o.value === selected;
          const delta = o.price_delta || o.sovrapprezzo || 0;
          return (
            <button key={o.value} onClick={() => onSelect(o.value)}
              data-testid={`opt-${axis.name}-${o.value}`}
              className={`text-left p-1.5 rounded-sm border transition-all ${isSel ? "border-foreground bg-neutral-50" : "border-transparent hover:border-border"}`}>
              <div className="aspect-square rounded-sm mb-1 overflow-hidden bg-neutral-100"
                style={o.image_url ? { background: `center/cover no-repeat url(${o.image_url.startsWith("http") ? o.image_url : `${API.replace("/api", "")}${o.image_url}`})` } : { background: o.hex_color || "#e5e5e5" }} />
              <div className="text-[10px] font-medium truncate" title={o.label}>{o.label}</div>
              {delta > 0 && <div className="text-[9px] text-brand font-bold">+{fmtEuro(delta)}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ConfigRequestForm({ modello, tenant, axes, choices, prezzoIndicativo, leadTimeIndicativo, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Se cliente loggato, prefill (best-effort, non blocca se manca)
  useEffect(() => {
    try {
      const t = localStorage.getItem("token");
      if (t) {
        axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } })
          .then((r) => { setName(r.data.name || ""); setEmail(r.data.email || ""); })
          .catch(() => {});
      }
    } catch { /* ignore */ }
  }, []);

  const combination = {};
  for (const ax of axes) {
    const opt = (ax.options || []).find((o) => o.value === choices[ax.name]);
    combination[ax.name] = { code: choices[ax.name], label: opt?.label || "", delta: opt?.price_delta || opt?.sovrapprezzo || 0 };
  }

  const submit = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Nome e email sono obbligatori");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/config-requests`, {
        tenant_slug: tenant.slug,
        product_id: modello.id || modello.product_id || modello.sku_madre,
        sku_madre: modello.sku_madre,
        combination,
        prezzo_indicativo: prezzoIndicativo,
        lead_time_indicativo_gg: leadTimeIndicativo,
        customer_name: name, customer_email: email,
        customer_phone: phone, customer_notes: notes,
      });
      setDone(true);
    } catch (e) {
      toast.error("Errore invio: " + (e.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="request-form-modal">
      <div className="bg-white rounded-sm shadow-2xl max-w-lg w-full max-h-[92vh] overflow-auto">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg">Richiedi configurazione</h3>
          <button onClick={onClose} className="text-2xl leading-none">×</button>
        </div>

        {done ? (
          <div className="p-8 text-center space-y-4" data-testid="request-form-done">
            <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-600" strokeWidth={1.5} />
            <h4 className="font-heading text-xl">Richiesta inviata!</h4>
            <p className="text-sm text-muted-foreground">
              Riceverai una quotazione ufficiale entro 24-48h all&apos;indirizzo <strong>{email}</strong>.
              Se hai un account puoi seguire lo stato nella tua area riservata.
            </p>
            <button onClick={onClose} className="h-10 px-6 bg-foreground text-white rounded-sm text-sm">Chiudi</button>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="bg-neutral-50 border border-border rounded-sm p-3 text-xs">
              <div className="overline text-muted-foreground mb-2">La tua configurazione</div>
              <div className="font-medium mb-1">{modello.nome} · <span className="font-mono">{modello.sku_madre}</span></div>
              <ul className="space-y-0.5 text-muted-foreground">
                {Object.entries(combination).map(([k, v]) => (
                  <li key={k}>{k}: <strong className="text-foreground">{v.label}</strong>{v.delta > 0 && ` (+${fmtEuro(v.delta)})`}</li>
                ))}
              </ul>
              <div className="mt-2 pt-2 border-t border-border flex justify-between font-medium">
                <span>Prezzo indicativo</span>
                <span data-testid="form-price-preview">{fmtEuro(prezzoIndicativo)}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Nome*</label>
                <input value={name} onChange={(e) => setName(e.target.value)} data-testid="req-name"
                  className="w-full h-10 border border-border rounded-sm px-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Email*</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="req-email"
                  type="email" className="w-full h-10 border border-border rounded-sm px-2 text-sm" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Telefono</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="req-phone"
                  className="w-full h-10 border border-border rounded-sm px-2 text-sm" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Note (facoltative)</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="req-notes"
                  rows={3} placeholder="Es. preferirei consegna prima di X, vorrei un campione, ..."
                  className="w-full border border-border rounded-sm px-2 py-1.5 text-sm resize-y" />
              </div>
            </div>

            <div className="flex gap-2 pt-3 border-t border-border">
              <button onClick={onClose} className="flex-1 h-10 border border-border rounded-sm text-sm" data-testid="req-cancel">Annulla</button>
              <button onClick={submit} disabled={submitting}
                data-testid="req-submit"
                className="flex-1 h-10 bg-foreground text-white rounded-sm text-sm font-medium disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Invia richiesta
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
